import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Check, ShieldCheck } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { isWorkEmail, WORK_EMAIL_MESSAGE } from "@/lib/workEmail";
import { TERMS_MD } from "@/lib/terms";
import { TermsMarkdown } from "@/components/TermsMarkdown";

const DAY_ONE = [
  "Connect Salesforce, Zoho or Oracle and see live governance in minutes",
  "7-day trial — no payment at signup",
  "Email verification required before first login",
  "Continue with a plan later from Billing if you want full access after the trial",
];

export default function Signup() {
  const navigate = useNavigate();
  const { register, formatApiErrorDetail } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agree) {
      setError("Please accept the Terms and Conditions to continue.");
      return;
    }
    if (!isWorkEmail(email)) {
      setError(WORK_EMAIL_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      await register({ name, email, password, plan: "starter", accept_terms: true });
      try { sessionStorage.setItem("gx_verify_email", email); } catch { /* ignore */ }
      navigate("/check-email", { replace: true, state: { email } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell wide promo="signup">
      <div data-testid="signup-page">
        <p className="gx-label auth-fade-up">Get started</p>
        <h2 className="auth-fade-up auth-delay-100 mt-2 font-display text-3xl font-bold tracking-tight lg:text-4xl">
          Create your workspace
        </h2>
        <p className="auth-fade-up auth-delay-200 mt-2 text-sm text-muted-foreground">
          Use a company email. No card, no plan to pick — we'll send a verification link before you can sign in.
        </p>

        <div
          className="mt-6 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm font-medium text-primary"
          data-testid="trial-banner"
        >
          <ShieldCheck className="h-4 w-4" />
          7 days free · no payment required · verification mandatory
        </div>

        <div className="mt-5 rounded-xl border border-border bg-card p-4" data-testid="signup-day-one">
          <p className="gx-label mb-3">What you get on day one</p>
          <ul className="space-y-2">
            {DAY_ONE.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-foreground/90">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-6">
          <div className="grid gap-5 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="h-12 rounded-xl bg-card shadow-sm"
                data-testid="signup-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 rounded-xl bg-card shadow-sm"
                data-testid="signup-email-input"
              />
              <p className="text-[11px] text-muted-foreground" data-testid="signup-email-hint">
                Business address only — not Gmail, Outlook, Yahoo, or other personal inboxes.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="h-12 rounded-xl bg-card shadow-sm"
                data-testid="signup-password-input"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label htmlFor="signup-terms">Terms and Conditions</Label>
            <div
              id="signup-terms"
              tabIndex={0}
              className="max-h-72 overflow-y-auto rounded-xl border border-border bg-card p-4"
              data-testid="signup-terms"
            >
              <TermsMarkdown source={TERMS_MD} compact />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Full copy also on the{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline-offset-4 hover:underline"
                data-testid="terms-link"
              >
                Terms and Conditions
              </Link>{" "}
              page.
            </p>
          </div>

          <div className="flex items-start gap-3" data-testid="terms-checkbox-row">
            <Checkbox
              id="accept-terms"
              checked={agree}
              onCheckedChange={(v) => setAgree(!!v)}
              className="mt-0.5"
              data-testid="signup-terms-checkbox"
            />
            <Label htmlFor="accept-terms" className="text-sm font-normal leading-relaxed text-muted-foreground">
              I have read and agree to the Terms and Conditions and understand that email verification is required to activate my account.
            </Label>
          </div>

          {error && (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="signup-error"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || !agree}
            className="h-12 w-full rounded-xl text-sm font-semibold shadow-md"
            data-testid="signup-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account & verify email"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-primary underline-offset-4 hover:underline"
            data-testid="link-to-login"
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
